import React from "react";
import { Section, Reveal } from "@/components/network/NetworkAtoms";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useFundingCta } from "./FundingCta";

/** The questions people actually ask, answered as we'd answer them on the phone. */
export const FAQS = [
  {
    q: "Are you a lender?",
    a: "No. Connexis is a funding desk — we take your application to the lenders and funders on our panel, and we "
     + "work out which of them can genuinely help. The money, the terms and the final decision always come from "
     + "the funder, and you'll know who they are before anything is agreed.",
  },
  {
    q: "What does it cost me?",
    a: "Nothing. There's no fee for the application, no fee for the advice and no charge for the conversation. When "
     + "a funder writes the deal, they pay us a commission — that's how we're paid. If a particular case ever "
     + "involved a fee, you'd be told the exact amount in writing before you committed to anything.",
  },
  {
    q: "Will applying hurt my credit score?",
    a: "Talking to us doesn't touch your credit file. We don't run a credit search to review your application or to "
     + "give you a view on your options. A funder may run a search when they formally assess your case — you'll be "
     + "told before that happens, and many funders start with a 'soft' search that doesn't affect your score.",
  },
  {
    q: "How quickly could I have the money?",
    a: "For straightforward cases, decisions usually come back within 24 to 48 hours, and some funders decide the "
     + "same day. Funds are typically released one to three working days after you accept. Property, development "
     + "and larger structured transactions run to valuations and credit committees — you'll have that timetable "
     + "on day one.",
  },
  {
    q: "Who sees my information?",
    a: "Our funding desk, and the funders actively considering your application. Nobody else. We are not a lead "
     + "generator and we never sell your details on. Your documents are held in encrypted storage rather than "
     + "circulated by email, and every access is logged.",
  },
  {
    q: "What documents will I need?",
    a: "Usually the last three to six months of business bank statements, your most recent filed accounts, and a "
     + "sentence or two about what the money is for. Depending on the route we may ask for management figures, an "
     + "aged debtors list, or a quote for the asset you're buying. We'll only ever ask for what a funder actually "
     + "needs to decide.",
  },
  {
    q: "I've been declined elsewhere — is it worth applying?",
    a: "Often, yes. A decline usually means you didn't fit that particular lender's rules, not that your business "
     + "isn't fundable. Different funders take completely different views on sector, trading history, adverse credit "
     + "and security. That said, if we don't think anyone will fund it, we'll tell you straight and explain what "
     + "would need to change first.",
  },
  {
    q: "Do I have to accept an offer?",
    a: "No. There's no obligation at any point. If you get terms and decide against them, that's the end of it — "
     + "there's nothing to pay and no pressure from us. We'd rather you walked away than took money that hurt "
     + "your business.",
  },
  {
    q: "Can I speak to an actual person?",
    a: "Yes, and you'll have the same one throughout. Every application is read by a human on the day it arrives, "
     + "and you deal with a named contact from the first conversation to the day you draw the money down.",
  },
  {
    q: "How much can my business borrow?",
    a: "It depends on the route. Unsecured lending is sized against turnover and serviceability; invoice finance "
     + "against your sales ledger; asset finance against the asset; property and development against value and "
     + "cost. We place facilities from £25,000 to £500m and beyond, including club and syndicated structures. "
     + "Tell us the number you have in mind and we'll tell you what it takes to get there.",
  },
  {
    q: "Do you only work with limited companies?",
    a: "No — sole traders and partnerships can be funded too, though the range of funders is narrower and personal "
     + "guarantees are more common. We'll be upfront about what that means for you before you apply.",
  },
  {
    q: "What if my business is new?",
    a: "Limited trading history narrows the field but doesn't close it: revenue-based advances, asset finance and "
     + "government-backed start-up lending all exist for younger businesses. We'd rather tell you 'not yet, and "
     + "here's what to fix' than send you round eight funders for eight declines.",
  },
  {
    q: "An introducer sent me here. Do they see my information?",
    a: "The introducer who referred you can see the progress of your case, because they're the person who knows you "
     + "and often helps push it along. They don't see anything a funder sends us in confidence, and your documents "
     + "stay in the vault.",
  },
  {
    q: "How do I check on my application?",
    a: "Ask your contact any time — or use the tracking link we send when your application is submitted, which "
     + "shows the current stage without you having to phone anybody.",
  },
  {
    q: "Are you regulated?",
    a: "Most commercial finance for limited companies sits outside the Financial Conduct Authority's consumer "
     + "regime, so this type of business lending is generally unregulated. Where a case involves a regulated "
     + "product, we only work with appropriately authorised parties, and we'll tell you clearly which protections "
     + "do and don't apply to your facility before you sign anything.",
  },
];

export default function FundingFaq({ items = FAQS }) {
  const { openDesk } = useFundingCta();

  return (
    <Section
      testId="funding-faq"
      eyebrow="Questions"
      title="Everything people ask before they apply"
      lede="If your question isn't here, ask it. Better answered now than halfway through an application."
    >
      <div className="mt-10 grid lg:grid-cols-[1.25fr_0.75fr] gap-10 lg:gap-16 items-start">
        <Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="funding-faq-list">
            {items.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-[15.5px] sm:text-[16px] font-medium text-white hover:text-[#7da9ff] hover:no-underline py-5"
                                  data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[14.5px] leading-relaxed text-slate-300 pb-6 pr-4"
                                  data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>

        <Reveal delay={0.1} className="lg:sticky lg:top-28">
          <div className="rounded-3xl border border-white/10 bg-[#0055FF]/[0.06] p-7">
            <h3 className="font-cabinet text-[22px] font-semibold text-white tracking-[-0.02em]">
              Rather speak to someone?
            </h3>
            <p className="mt-3 text-[14.5px] leading-relaxed text-slate-300">
              Ask us anything — including whether borrowing is the right move at all. Five minutes with someone who
              places this every day is worth an afternoon of reading.
            </p>
            <button onClick={openDesk} data-testid="faq-desk-cta"
                    className="mt-6 w-full inline-flex items-center justify-center h-12 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[14.5px] font-semibold transition-colors">
              Request a call back
            </button>
            <p className="mt-4 text-[12px] text-slate-400 leading-relaxed">
              Or email <a href="mailto:ops@connex.is" className="text-white underline underline-offset-4">ops@connex.is</a> —
              we answer within one working day.
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
